import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';
const STATUS_RANK={
  INSUFFICIENT_DATA:0,
  CONSTRUCTIVE:1,
  REVIEW:2,
  ROTATION_RISK:3,
  RISK_ELEVATED:4
};

function validStatus(x){ return Object.prototype.hasOwnProperty.call(STATUS_RANK,x); }
function normalizeContext(x){
  x=x||{};
  return Object.freeze({
    status:validStatus(x.status)?x.status:'INSUFFICIENT_DATA',
    breadthState:x.breadth&&x.breadth.state?x.breadth.state:(x.breadthState||null),
    dispersionState:x.dispersion&&x.dispersion.state?x.dispersion.state:(x.dispersionState||null),
    rotationState:x.rotation&&x.rotation.state?x.rotation.state:(x.rotationState||null),
    verifiedRatio:typeof x.verifiedRatio==='number'?x.verifiedRatio:
      (x.coverage&&typeof x.coverage.verifiedRatio==='number'?x.coverage.verifiedRatio:null),
    asOf:x.asOf||null
  });
}

function evidenceChanges(prev,next){
  const p=normalizeContext(prev), n=normalizeContext(next);
  const changes=[];
  if(p.breadthState!==n.breadthState) changes.push(Object.freeze({dimension:'BREADTH',from:p.breadthState,to:n.breadthState}));
  if(p.dispersionState!==n.dispersionState) changes.push(Object.freeze({dimension:'DISPERSION',from:p.dispersionState,to:n.dispersionState}));
  if(p.rotationState!==n.rotationState) changes.push(Object.freeze({dimension:'ROTATION',from:p.rotationState,to:n.rotationState}));
  if(p.status!==n.status) changes.push(Object.freeze({dimension:'CONTEXT_STATUS',from:p.status,to:n.status}));
  return Object.freeze(changes);
}

function transition(prev,next){
  const p=normalizeContext(prev), n=normalizeContext(next);
  const changes=evidenceChanges(p,n);
  const rankDelta=STATUS_RANK[n.status]-STATUS_RANK[p.status];
  const evidenceCount=changes.filter(c=>c.dimension!=='CONTEXT_STATUS').length;
  const verificationKnown=typeof n.verifiedRatio==='number';
  const verifiedEnough=verificationKnown && n.verifiedRatio>=0.60;

  let state='NO_MEANINGFUL_CHANGE';
  let direction='FLAT';
  if(rankDelta>0) direction='DETERIORATING';
  else if(rankDelta<0) direction='IMPROVING';

  // Regime change requires more than one independent contextual dimension.
  if(evidenceCount>=2 && verifiedEnough && rankDelta!==0){
    state='REGIME_CHANGE_CONFIRMED';
  }else if((evidenceCount>=1 || rankDelta!==0) && verifiedEnough){
    state='CHANGE_REVIEW';
  }else if((evidenceCount>=1 || rankDelta!==0) && !verifiedEnough){
    state='UNVERIFIED_CHANGE';
  }

  return Object.freeze({
    state,
    direction,
    from:p.status,
    to:n.status,
    rankDelta,
    evidenceCount,
    verifiedEnough,
    changes,
    priceOnly:false,
    tradeSignal:false
  });
}

function alertDelta(previousAlerts,currentAlerts){
  const prev=Array.isArray(previousAlerts)?previousAlerts:[];
  const curr=Array.isArray(currentAlerts)?currentAlerts:[];
  const key=a=>`${a.symbol||''}|${a.region||''}|${a.priority||''}`;
  const prevSet=new Set(prev.map(key));
  const currSet=new Set(curr.map(key));

  const added=curr.filter(a=>!prevSet.has(key(a)));
  const removed=prev.filter(a=>!currSet.has(key(a)));

  return Object.freeze({
    added:Object.freeze(added.slice()),
    removed:Object.freeze(removed.slice()),
    addedCount:added.length,
    removedCount:removed.length
  });
}

function portfolioChangeReview(prevContext,nextContext,prevAlerts,currAlerts){
  const tr=transition(prevContext,nextContext);
  const delta=alertDelta(prevAlerts,currAlerts);

  let attention='NONE';
  if(tr.state==='REGIME_CHANGE_CONFIRMED' && delta.addedCount>0) attention='HIGH';
  else if(tr.state==='REGIME_CHANGE_CONFIRMED') attention='MEDIUM';
  else if(tr.state==='CHANGE_REVIEW' && delta.addedCount>0) attention='MEDIUM';
  else if(tr.state==='UNVERIFIED_CHANGE') attention='REVIEW';

  return Object.freeze({
    attention,
    transition:tr,
    newlyAffected:Object.freeze(delta.added.slice()),
    noLongerFlagged:Object.freeze(delta.removed.slice()),
    personalImpact:delta.addedCount>0,
    tradeSignal:false,
    companyQualityOverride:false
  });
}

function uiSummary(review){
  const r=review||{};
  const t=r.transition||{};
  const newly=Array.isArray(r.newlyAffected)?r.newlyAffected:[];
  return Object.freeze({
    attention:r.attention||'NONE',
    headline:
      t.state==='REGIME_CHANGE_CONFIRMED'
        ?`Global regime change: ${t.from||'UNKNOWN'} → ${t.to||'UNKNOWN'}`
        :t.state==='CHANGE_REVIEW'
          ?`Global context change needs review`
          :t.state==='UNVERIFIED_CHANGE'
            ?`Possible global context change — verification incomplete`
            :`No meaningful global regime change`,
    newlyAffectedCount:newly.length,
    symbols:Object.freeze(newly.map(x=>x.symbol).filter(Boolean)),
    tradeSignal:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const prev={
    status:'CONSTRUCTIVE',
    breadth:{state:'BROAD_POSITIVE'},
    dispersion:{state:'LOW'},
    rotation:{state:'BROADENING'},
    coverage:{verifiedRatio:1}
  };
  const next={
    status:'RISK_ELEVATED',
    breadth:{state:'BROAD_NEGATIVE'},
    dispersion:{state:'HIGH'},
    rotation:{state:'NARROWING'},
    coverage:{verifiedRatio:.86}
  };

  let tr=transition(prev,next);
  ok('multi-dimensional deterioration confirms regime change',tr.state==='REGIME_CHANGE_CONFIRMED');
  ok('direction detected as deteriorating',tr.direction==='DETERIORATING');
  ok('independent evidence counted',tr.evidenceCount===3);
  ok('verification gate passed',tr.verifiedEnough===true);
  ok('never emits trade signal',tr.tradeSignal===false);
  ok('price-only flag is false by design',tr.priceOnly===false);

  tr=transition(prev,{
    status:'REVIEW',
    breadth:{state:'BROAD_POSITIVE'},
    dispersion:{state:'LOW'},
    rotation:{state:'BROADENING'},
    coverage:{verifiedRatio:1}
  });
  ok('status-only move is review not regime change',tr.state==='CHANGE_REVIEW');

  tr=transition(prev,{
    status:'RISK_ELEVATED',
    breadth:{state:'BROAD_NEGATIVE'},
    dispersion:{state:'HIGH'},
    rotation:{state:'NARROWING'},
    coverage:{verifiedRatio:.4}
  });
  ok('low verification blocks confirmation',tr.state==='UNVERIFIED_CHANGE');

  const prevAlerts=[
    {symbol:'AAA',region:'china',priority:'HIGH'},
    {symbol:'BBB',region:'europe',priority:'MEDIUM'}
  ];
  const currAlerts=[
    {symbol:'AAA',region:'china',priority:'HIGH'},
    {symbol:'CCC',region:'china',priority:'HIGH'}
  ];
  const d=alertDelta(prevAlerts,currAlerts);
  ok('newly affected holding detected',d.addedCount===1&&d.added[0].symbol==='CCC');
  ok('resolved holding detected',d.removedCount===1&&d.removed[0].symbol==='BBB');

  const review=portfolioChangeReview(prev,next,prevAlerts,currAlerts);
  ok('confirmed regime plus new portfolio impact gets high attention',review.attention==='HIGH');
  ok('personal impact is explicit',review.personalImpact===true);
  ok('company quality never overridden',review.companyQualityOverride===false);
  ok('review remains non-trading',review.tradeSignal===false);

  const ui=uiSummary(review);
  ok('ui exposes newly affected count',ui.newlyAffectedCount===1);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={
  VERSION,STATUS_RANK,normalizeContext,evidenceChanges,transition,
  alertDelta,portfolioChangeReview,uiSummary,selfTest
};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestGlobalRegimeChangeEngine=Object.freeze(API);

})(engineContext);
